import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, ListChecks, Calendar as CalendarIcon,
  FolderKanban, Users, Building2, FileBarChart2, Bell, LogOut, ShieldCheck, Menu, X, CalendarDays, Key, UserCog
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { ROLES, initials } from "../lib/utils";
import ChangePasswordModal from "./ChangePasswordModal";
import { useIdleLogout } from "../hooks/useIdleLogout";
import { toast } from "sonner";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Panel KPI", end: true, roles: ["administrador","coordinador","colaborador","lectura"] },
  { to: "/tareas", icon: ListChecks, label: "Tareas", roles: ["administrador","coordinador","colaborador","lectura"] },
  { to: "/planificador", icon: CalendarDays, label: "Planificador Semanal", roles: ["administrador","coordinador","colaborador","lectura"] },
  { to: "/calendario", icon: CalendarIcon, label: "Calendario", roles: ["administrador","coordinador","colaborador","lectura"] },
  { to: "/proyectos", icon: FolderKanban, label: "Proyectos", roles: ["administrador","coordinador","colaborador","lectura"] },
  { to: "/reportes", icon: FileBarChart2, label: "Reportes", roles: ["administrador","coordinador","lectura"] },
  { to: "/usuarios", icon: Users, label: "Usuarios", roles: ["administrador"] },
  { to: "/coordinadores", icon: UserCog, label: "Gestión de Coordinadores", roles: ["administrador"] },
  { to: "/areas", icon: Building2, label: "Áreas", roles: ["administrador"] },
  { to: "/auditoria", icon: ShieldCheck, label: "Auditoría", roles: ["administrador"] },
];

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifCount, setNotifCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const idle = useIdleLogout(() => {
    toast.warning("Sesión cerrada por inactividad (10 minutos sin actividad)");
    logout();
    navigate("/login");
  }, true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (!cancelled) setNotifCount(data.filter(n => !n.read).length);
      } catch {}
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  const items = NAV.filter(n => n.roles.includes(user.role));

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 flex flex-col transition-transform lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="h-16 px-5 flex items-center gap-3 border-b border-slate-200">
          <div className="brand-logo" aria-hidden="true" />
          <div className="leading-tight">
            <div className="font-heading font-bold text-sm text-slate-900">CRE-JPP</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Panel de Tareas KPI</div>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setMobileOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g,'-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-red-50 text-crejpp border-l-2 border-crejpp"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={2} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-slate-900 text-white text-xs font-semibold flex items-center justify-center shrink-0">
              {initials(user.name)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{user.name}</div>
              <div className="text-[11px] text-slate-500 uppercase tracking-wider">{ROLES[user.role]}</div>
            </div>
            <button
              data-testid="change-password-btn"
              onClick={() => setShowPwd(true)}
              className="p-2 rounded-md hover:bg-slate-100 text-slate-500 hover:text-crejpp"
              title="Cambiar contraseña"
            >
              <Key className="w-4 h-4" />
            </button>
            <button
              data-testid="logout-btn"
              onClick={() => { logout(); navigate("/login"); }}
              className="p-2 rounded-md hover:bg-slate-100 text-slate-500 hover:text-crejpp"
              title="Cerrar sesión"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Header */}
      <header className="sticky top-0 z-30 lg:pl-64 bg-white border-b border-slate-200 h-16 flex items-center px-6">
        <button className="lg:hidden mr-3 p-2 rounded-md hover:bg-slate-100" onClick={() => setMobileOpen(v => !v)} data-testid="mobile-menu-toggle">
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        <div className="flex-1 min-w-0" />
        <button
          onClick={() => navigate("/notificaciones")}
          data-testid="notifications-bell"
          className="relative p-2 rounded-md hover:bg-slate-100 text-slate-600"
          title="Notificaciones"
        >
          <Bell className="w-5 h-5" />
          {notifCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-crejpp text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {notifCount > 99 ? "99+" : notifCount}
            </span>
          )}
        </button>
      </header>

      {/* Backdrop */}
      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/30 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main */}
      <main className="lg:pl-64">
        <div className="p-6 sm:p-8 fade-in">
          <Outlet />
        </div>
      </main>

      <ChangePasswordModal open={showPwd} onClose={() => setShowPwd(false)} />

      {idle.warnOpen && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" data-testid="idle-warning">
          <div className="bg-white rounded-lg border border-slate-200 w-full max-w-sm p-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-md bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Seguridad</div>
                <h3 className="font-heading text-lg font-bold text-slate-900">Sesión por expirar</h3>
              </div>
            </div>
            <p className="text-sm text-slate-600">
              Por inactividad, su sesión se cerrará automáticamente en{" "}
              <b className="text-crejpp text-lg">{idle.secondsLeft}</b> segundos.
            </p>
            <p className="text-xs text-slate-500 mt-2">Cualquier interacción (movimiento del mouse, tecla o clic) mantendrá la sesión activa.</p>
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => { logout(); navigate("/login"); }}
                className="px-4 py-2 border border-slate-300 rounded-md text-sm bg-white hover:bg-slate-100"
                data-testid="idle-logout-now"
              >
                Cerrar sesión
              </button>
              <button
                onClick={idle.stayConnected}
                className="px-4 py-2 bg-crejpp hover:bg-crejpp-dark text-white rounded-md text-sm font-semibold"
                data-testid="idle-stay-connected"
              >
                Continuar conectado
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
